import * as React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { grey } from '@mui/material/colors';

const EXCEPTION_KEYWORDS =
  /delay|refused|damage|exception|redelivery|reattempt|failed delivery|missed pickup|held at/i;

const getShipmentException = (rowData, trackShipmentData) => {
  const status = rowData?.shipmentStatus;
  if (status && status.toUpperCase() === 'HOLD') {
    return { label: 'On Hold', tone: 'error' };
  }
  if (status === 'Cancelled') {
    return { label: 'Cancelled', tone: 'error' };
  }
  if (status !== 'Delivered') {
    const lines = trackShipmentData?.tracingLines || [];
    const latest = lines[lines.length - 1];
    if (latest?.description && EXCEPTION_KEYWORDS.test(latest.description)) {
      return {
        label: 'Exception reported',
        tone: 'warning',
        detail: latest.description
      };
    }
  }
  return null;
};


const adjustTracingLinesOrder = tracingLines => {
  if (tracingLines) {
    // Check the descriptions of the first and last elements
    const isPickupLast = /Pickup/i.test(
      tracingLines[tracingLines?.length - 1]?.description
    );
    const isDeliveredFirst = /Delivered/i.test(tracingLines[0]?.description);

    // Reverse the array if necessary
    if (isPickupLast && isDeliveredFirst) {
      return tracingLines?.reverse();
    }
    return tracingLines; // Keep as-is if already in correct order
  }
};
const generateSteps = (trackShipmentData, rowData) => {
  const steps = [];
  const adjustedTracingLines = adjustTracingLinesOrder(
    trackShipmentData?.tracingLines
  );

  const firstStep = adjustedTracingLines?.find(line =>
    /Picked Up|ACTUAL_PICKUP|PICK UP MADE|PickUp/i.test(line.description)
  );
  function formatPickupDate(dateString) {
    // Split date and time
    const parts = dateString.split(' ');
    if (
      rowData.carrierName === 'XPO' &&
      parts.length >= 5 &&
      parts[3] === '08:00' &&
      parts[4] === 'AM'
    ) {
      return `${parts[0]} ${parts[1]} ${parts[2]}`; // Remove time
    }
    // Check if the time exists and is exactly "12:00 AM"
    if (parts.length >= 5 && parts[3] === '12:00' && parts[4] === 'AM') {
      return `${parts[0]} ${parts[1]} ${parts[2]}`; // Remove time
    }

    return dateString; // Keep original if time is different
  }
  // if (firstStep) {
  steps.push({
    label: 'Picked Up',
    date: formatPickupDate(trackShipmentData.pickupDate),
    pickupLoc:
      rowData.originCity +
      ' ' +
      rowData.originState +
      ' ' +
      rowData.originZipCode,
    completed: true // Always completed because it's the first step
  });
  // }

  // Step 2: In Transit (no date needed)
  steps.push({
    label: 'In Transit',
    date: '', // No date
    completed: true // Mark as incomplete
  });

  // Step 3: At Destination Terminal (no date needed)
  steps.push({
    label: 'At Destination Terminal',
    date: '', // No date
    completed: true // Mark as incomplete
  });

  // Step 4: Last step with "Delivered" or "Actual Delivery" match
  const lastStep = adjustedTracingLines?.find(line =>
    /Delivered|ACTUAL_DELIVERY/i.test(line.description)
  );

  // if (lastStep) {
  steps.push({
    label: 'Delivered',
    date:
      rowData?.shipmentStatus == 'Delivered'
        ? trackShipmentData.deliveryDate
        : '',
    deliveryLoc:
      trackShipmentData?.shipmentStatus === 3
        ? ''
        : rowData.destinationCity +
          ' ' +
          rowData.destinationState +
          ' ' +
          rowData.destinationZipCode,
    completed: true // Mark as completed
  });
  // }

  return steps;
};

function getDotColor(status, stepIndex, exception) {
  if (exception) {
    // Picked Up already happened; later steps reflect the exception tone
    return stepIndex === 0 ? 'success' : exception.tone;
  }
  if (status === 3) {
    // In Transit status: first two dots warning, rest grey
    return stepIndex <= 1 ? 'warning' : 'grey';
  }
  return 'success';
}

function getLineColor(status, lineIndex, exception) {
  // lineIndex is the index of the left node (line sits between node lineIndex and lineIndex+1)
  if (exception) {
    return lineIndex === 0 ? 'success.main' : `${exception.tone}.main`;
  }
  if (status === 3) {
    return lineIndex === 0 ? 'warning.main' : grey[300];
  }
  return 'success.main';
}

function ExceptionBanner({ exception, px }) {
  if (!exception) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, px }}>
      <WarningAmberIcon
        sx={{
          fontSize: 16,
          color: exception.tone === 'error' ? 'error.main' : 'warning.main'
        }}
      />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: exception.tone === 'error' ? 'error.main' : 'warning.main'
        }}
      >
        {exception.label}
      </Typography>
      {exception.detail && (
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          — {exception.detail}
        </Typography>
      )}
    </Box>
  );
}

function dotBgColor(dotColor) {
  return dotColor === 'warning'
    ? 'warning.main'
    : dotColor === 'error'
      ? 'error.main'
      : dotColor === 'grey'
        ? grey[400]
        : 'success.main';
}

export default function TrackShipmentDetails(props) {
  const trackShipmentData = props?.trackShipmentData;
  const rowData = props?.rowData;
  const variant = props?.variant || 'horizontal';
  const status = trackShipmentData?.shipmentStatus;
  const steps = generateSteps(trackShipmentData, rowData);
  const exception = getShipmentException(rowData, trackShipmentData);

  if (variant === 'vertical') {
    return (
      <Box sx={{ width: '100%' }}>
        <ExceptionBanner exception={exception} px={0} />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {steps.map((step, index) => {
            const dotColor = getDotColor(status, index, exception);
            const isLast = index === steps.length - 1;
            return (
              <Box key={index} sx={{ display: 'flex', gap: 1.2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: dotBgColor(dotColor),
                      flexShrink: 0
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 14, color: '#fff' }} />
                  </Box>
                  {!isLast && (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        minHeight: 22,
                        bgcolor: getLineColor(status, index, exception),
                        my: 0.3
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ pb: isLast ? 0 : 1.2 }}>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}
                  >
                    {step.label}
                  </Typography>
                  {(step.date || step.pickupLoc || step.deliveryLoc) && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: '#64748b',
                        lineHeight: 1.4,
                        mt: 0.1
                      }}
                    >
                      {step.date}
                      {(step.pickupLoc || step.deliveryLoc) && (
                        <>
                          {step.date && <br />}
                          {step.pickupLoc || step.deliveryLoc}
                        </>
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', py: 0.25 }}>
      <ExceptionBanner exception={exception} px={1} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          minWidth: 'max-content',
          px: 1
        }}
      >
        {steps.map((step, index) => {
          const dotColor = getDotColor(status, index, exception);
          return (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 100
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#1e293b',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    mb: 0.2
                  }}
                >
                  {step.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: '#64748b',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    mb: 0.3,
                    lineHeight: 1.3
                  }}
                >
                  {step.date || ''}
                  {(step.pickupLoc || step.deliveryLoc) && (
                    <>
                      {step.date && <br />}
                      <b>{step.pickupLoc || step.deliveryLoc}</b>
                    </>
                  )}
                </Typography>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: dotBgColor(dotColor),
                    flexShrink: 0
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 15, color: '#fff' }} />
                </Box>
              </Box>

              {index < steps.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 50,
                    maxWidth: 140,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: getLineColor(status, index, exception),
                    mb: '9px',
                    mx: 0.5
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
